import {
  fetchStandardData,
  fetchPreviewTree as fetchPreviewTreeApi,
  destroyStandardNode as destroyStandardNodeApi,
  createNestedStandard as createNestedStandardApi,
  cleanStandardCache as cleanStandardCacheApi,
  updateStandardNodeOrder as updateNodeOrderApi,
  toggleNodeEnabled as toggleNodeEnabledApi,
  getExportStandardUrl as getExportStandardUrlApi,
} from '@vt/apis/lib-ex/v2/standard'
import {
  fetchBindRules as fetchBindRulesApi,
  fetchBindNodeRuleList as fetchBindNodeRuleListApi,
  fetchFieldBindingTable as fetchFieldBindingTableApi,
  destroyBatchAreas as destroyAreasApi,
  fetchBindUserRule as fetchBindUserRuleApi,
  switchBindUserRule as switchBindUserRuleApi,
  fetchRuleObjectList as fetchRuleObjectListApi,
  batchBindRuleUserList as bindRuleUserListApi,
  fetchRuleObjectUserList as fetchRuleObjectUserListApi,
  createBindUserRule as createBindUserRuleApi,
} from '@vt/apis/lib-ex/v2/area'
import {
  fetchTableData as fetchTableDataApi,
  createTbData as createTbDataApi,
  updateTbData as updateTbDataApi,
  fetchObjectIdsAtTable as fetchObjectIdsAtTableApi,
} from '@vt/apis/lib-ex/v2/tables'
import { listToTree, listToTable, filterLeaf } from '@ow/utils'
import { isArray, head, startsWith, get } from 'lodash-es'
import Base from './base'

export default class Standard extends Base {
  constructor(appId, tbNameId, vtProject) {
    super(appId, tbNameId, vtProject)
    this.idFiledName = 'objectId'
    this.dbSpecPrefix = 'tbi_standards'
  }

  fetchAll() {
    return fetchStandardData(this.appId).then(async (data) => {
      this.data = data
      await this.store(data)
      this.scope = 'all'
      return data
    })
  }

  // 目前接口报错了
  fetchByParentId(id) {
    const filters = {
      logicType: 'and',
      childQueries: {
        queryType: 'just',
        yacQueryOperate: {
          operator: 'contain',
          left: {
            slug: 'parentIds',
          },
          right: {
            value: id,
          },
        },
      },
    }
    return fetchStandardData(this.appId, filters).then(async (data) => {
      await this.store(data)
      this.scope = 'all'
      return data
    })
  }

  fetchEffectiveData(parentId) {
    return this.fetchAll().then((data) => {
      let arr = data.filter((v) => !v.isDisableNode)
      if (parentId) {
        arr = data.filter(
          (v) =>
            (v.parentIds && v.parentIds.includes(parentId)) ||
            v.objectId === parentId
        )
      }
      return arr
    })
  }

  fetchTreeData() {
    return this.fetchAll().then((data) => {
      return listToTree(
        data.map((v) => {
          return { ...v, id: v.objectId }
        })
      )
    })
  }

  fetchEffectiveTreeData() {
    return this.fetchAll().then((data) => {
      return listToTree(
        data
          .map((v) => {
            return { ...v, id: v.objectId }
          })
          .filter((v) => !v.isDisableNode)
      )
    })
  }

  async fetchAreaBindStandard(areas) {
    if (!areas) return []
    const areaId = Array.isArray(areas) ? areas[areas.length - 1] : areas
    try {
      const rules = await fetchBindRulesApi({
        createdById: this.tbId,
        started: true,
      })

      if (!rules.content || !rules.content.length) {
        return []
      }

      const bindRuleId = rules.content[0].id

      const nodeList = await fetchBindNodeRuleListApi({
        bindRuleId,
        leaderBindId: areaId,
      })

      return nodeList.map((node) => node.followerBindId)
    } catch (err) {
      console.log(err)
      return []
    }
  }

  async fetchEffectiveBindTreeData(sourceTbId) {
    const bindIds = await fetchObjectIdsAtTableApi(
      this.appId,
      this.tbId,
      sourceTbId
    )

    return this.fetchEffectiveData().then((data) => {
      const arr = data.map((v) => {
        return { ...v, id: v.objectId }
      })

      if (bindIds && bindIds.length) {
        return listToTree(arr.filter((v) => bindIds.includes(v.id)))
      }
      return listToTree(arr)
    })
  }

  afterDataChanged(tbData) {
    this.store(tbData)
    this.dealLazyData(tbData)
  }

  getTreeTable(parentId, { filterStandardIds }) {
    return this.fetchAll().then((data) => {
      const finalData = filterStandardIds
        ? data.filter((v) => !filterStandardIds.includes(v.objectId))
        : data
      let arr = finalData
      if (parentId) {
        arr = finalData.filter(
          (v) =>
            (v.parentIds && v.parentIds.includes(parentId)) ||
            v.objectId === parentId
        )
      }
      return listToTable(
        arr.map((v) => {
          return { ...v, id: v.objectId }
        })
      )
    })
  }

  // eslint-disable-next-line
  filterStandards(standards, limit) {
    return filterLeaf(standards, limit)
  }

  /**
   * 按id获取数据
   * @param {*} id
   * @returns
   */
  async fetchById(id) {
    const datum = await this.fetchByIds(id)

    return isArray(datum) ? head(datum) : null
  }

  /**
   * @private 通过接口获取全部级联数据
   * @param rawParams 参数
   * @param {*} rawParams.filters
   * @param {*} rawParams.sorts
   * @param {*} rawParams.pageSize
   * @param {*} rawParams.pageNum
   * @returns
   */
  fetchTbDataRaw({ filters, sorts = {}, pageSize, pageNum }) {
    return fetchTableDataApi(
      this.appId,
      this.tbId,
      {
        filters,
        sorts,
      },
      {
        size: pageSize,
        page: pageNum,
      }
    )
  }

  /**
   * 获取全部级联数据
   * @param params 参数
   * @param {*} rawParams.filters
   * @param {*} rawParams.sorts
   * @param {*} rawParams.pageSize
   * @param {*} rawParams.pageNum
   * @returns listToTree之后的结果
   */
  fetchTbData(params) {
    return this.fetchTbDataRaw(params).then(async (res) => {
      const { tbData, total } = res
      this.total = total
      // this.afterDataChanged(tbData)
      const dealedData = await this.dealLazyData(tbData)
      return dealedData
    })
  }

  /**
   * 添加多级指标的预览
   * @param {*} standardList — 多级指标列表
   * @returns
   */
  fetchPreviewTree(standardList) {
    return fetchPreviewTreeApi(this.appId, standardList).then(async (data) => {
      return listToTree(
        data.map((v) => {
          return { ...v, id: v.objectId }
        })
      )
    })
  }

  /**
   *添加指标
   * @param item — 指标信息
   * @returns
   */
  createTbData(item) {
    return createTbDataApi(this.appId, this.tbId, item).then((newItem) => {
      return newItem
    })
  }

  /**
   * 修改指标
   * @param {*} itemId 要修改的数据id
   * @param {*} changedValues 要修改的数据
   * @returns
   */
  updateTbData(item) {
    return updateTbDataApi(this.appId, this.tbId, item).then((updateItem) => {
      return updateItem
    })
  }

  /**
   * 删除指标
   * @param {*} objectId 要删除的数据id
   * @returns
   */
  destroyTbData(objectId) {
    return destroyStandardNodeApi(this.appId, this.tbId, objectId).then(
      (res) => {
        return res
      }
    )
  }

  /**
   * 获取绑定指标的映射
   * @param {*} bindTbId 绑定的表单id
   * @returns {bindMap: {objectId:{bindSlug: slug}}}
   */
  getBindMap(bindTbId) {
    const ditto = this.getDitto(bindTbId)

    const { fields } = ditto || { fields: [] }

    const result = fields.filter((field) => {
      return startsWith(field.slug, 'bind_standard') && field.isBindStandard
    })

    const bindMap = {}

    result.forEach((res) => {
      const objectId = res.slug.split('_')[2]
      bindMap[objectId] = { bindSlug: res.selfRefSlug }
    })

    return {
      bindMap,
    }
  }

  /**
   * 获取单个指标绑定的slug
   * @param {*} bindTbId 绑定的表单id
   * @param {*} objectId 指标id
   * @returns {bindSlug: { bindSlug: slug }}
   */
  getBindSlug(bindTbId, objectId) {
    const { bindMap } = this.getBindMap(bindTbId)
    return { bindSlug: get(bindMap[objectId], 'bindSlug', null) }
  }

  /**
   * 添加多级指标
   * @param {*} parentId 父级id,没有可填null
   * @param {*} list 添加的指标数据
   * @returns
   */
  batchCreateTbData(parentId, list) {
    return createNestedStandardApi(this.appId, this.tbId, parentId, list).then(
      (res) => {
        return res
      }
    )
  }

  /**
   * 获取指标绑定用户规则
   * @param {string} ruleName 规则名称
   * @param {*} targetTbId 绑定的表单id
   * @returns
   */
  fetchBindUserRule(targetTbId, page, size) {
    return fetchBindUserRuleApi(
      this.appId,
      this.tbId,
      targetTbId,
      page,
      size
    ).then((res) => {
      return res
    })
  }

  /**
   * 创建指标绑定用户规则
   * @param {Object} rule 规则参数
   * @param {string} rule.ruleId 规则参数
   * @param {string} rule.ruleName 规则名称
   * @param {string} rule.targetTbId 绑定的表单id
   * @param {*} rule.ruleConfig 绑定用户
   * @returns
   */
  async createBindUserRule({ ruleId, ruleName, targetTbId, ruleConfig }) {
    let id
    if (!ruleId) {
      try {
        const res = await createBindUserRuleApi(
          this.appId,
          this.tbId,
          ruleName,
          targetTbId
        )

        id = res.id
      } catch (err) {
        return { success: false, message: '创建失败' }
      }
    } else {
      id = ruleId
    }

    const objectIds = Object.keys(ruleConfig)

    if (!objectIds.length) {
      return { success: true, message: '创建成功' }
    }

    try {
      await bindRuleUserListApi(this.appId, id, ruleConfig)

      return { success: true, message: '创建成功' }
    } catch (err) {
      return { success: false, message: '规则绑定用户失败' }
    }
  }

  /**
   * 启用/关闭指标绑定用户规则
   * @param {*} appId 项目Id
   * @param {*} ruleId 规则Id
   * @param {boolean} enable 开启关闭
   * @returns
   */
  switchBindUserRule(ruleId, enable) {
    return switchBindUserRuleApi(this.appId, ruleId, enable)
  }

  /**
   * 获取规则下绑定的objectId
   * @param {*} ruleId 规则Id
   * @returns
   */
  fetchRuleObjectList(ruleId) {
    return fetchRuleObjectListApi(this.appId, ruleId)
  }

  /**
   * 获取规则下某个objectId绑定的用户
   * @param {*} ruleId 规则Id
   * @param {*} objectId 绑定对象id
   * @returns
   */
  fetchRuleObjectUserList(ruleId, objectId) {
    return fetchRuleObjectUserListApi(this.appId, ruleId, objectId)
  }

  /**
   * 清除指标缓存
   * @returns
   */
  cleanTbDataCache() {
    return cleanStandardCacheApi(this.appId).then((res) => {
      return res
    })
  }

  /**
   * 修改节点顺序
   * @param {*} objectList 指标对象列表 eg. {object1: 1, object2: 4}
   * @returns
   */
  updateNodeOrder(objectList) {
    return updateNodeOrderApi(this.appId, this.tbId, objectList)
  }

  /**
   * 节点启用禁用
   * @param {*} objectId
   * @param {*} disable
   * @returns
   */
  toggleNodeEnabled({ objectId, disable }) {
    return toggleNodeEnabledApi(this.appId, this.tbId, objectId, disable)
  }

  /**
   * 获取绑定点位的表单
   * @returns
   */
  fetchFieldBindingTable() {
    return fetchFieldBindingTableApi(this.appId, this.tbId)
  }

  /**
   * 获取全部绑定数据
   * @param {*} useInfo
   */
  fetchBindedTableData(useInfo, standardList, includeDeleted = true) {
    const fetchAllData = async (
      page,
      { useTableId, filters, returnSlugs },
      pre = []
    ) => {
      const res = await fetchTableDataApi(
        this.appId,
        useTableId,
        {
          filters,
        },
        { returnSlugs, size: 1000, page }
      )

      const final = pre.concat(res.tbData)

      if (final.length < res.total) {
        return fetchAllData(
          page + 1,
          { useTableId, filters, returnSlugs },
          final
        )
      }

      return final
    }

    return Promise.all(
      useInfo.map(({ useSlugs, useTableId }) => {
        const queries = [
          {
            slug: useSlugs[0],
            value: standardList,
            queryType: ['containArray'],
          },
        ]
        const isFakeDeletedField = this.tbs[useTableId].fields.find(
          (v) => v.slug === 'isFakeDeleted'
        )
        if (!includeDeleted && isFakeDeletedField) {
          queries.push({
            slug: 'isFakeDeleted',
            value: true,
            queryType: ['notEqual'],
          })
        }
        const filters = {
          queries,
          logicType: 'and',
        }
        const returnSlugs = ['objectId', 'tableDataId', ...useSlugs]
        return fetchAllData(0, {
          useTableId,
          filters,
          returnSlugs,
        })
      })
    )
  }

  destroyAreas(queue) {
    return destroyAreasApi(this.appId, this.tbId, 'STANDARD', queue.flat(1))
  }

  /**
   * 导出指标
   * @param {boolean} obtainDisableNode 是否获取禁用指标
   * @returns
   */
  getExportUrl(obtainDisableNode = false) {
    return getExportStandardUrlApi(this.appId, obtainDisableNode)
  }
}

Standard.modelName = 'standard'
